/**
 * ReadOnlyModeBanner (E11 Inc-2, STATUS-export.md D3) — the app-top banner shown
 * when the CURRENT instance is in read-only (export-only) tenant mode.
 *
 * Rendered from the reserved HeaderBanner slot (app top, all pages) beside the
 * calendar reconnect banner. Self-gating: renders nothing unless there's an
 * instanceId, the user is on an operator pack in CRM mode, and /crm/me reports
 * read_only === true. PERSISTENT (no dismiss): read-only is a standing
 * contractual condition — writes are 423 server-side until a SkyPoint platform
 * admin lifts the flag, so a dismissable banner would hide a live constraint.
 *
 * Reads the flag from the SAME getCrmMe query the landing redirect already
 * fires (RTK Query dedupes), so this adds no extra round-trip. role="status"
 * (informational standing state, not an actionable alert).
 */
import { getInstanceIdFromUrl } from "../../utils/instanceId";
import { usePacks } from "../packs/PackContext";
import { useGetCrmMeQuery } from "../../store/api/crmMeApi";
import { SkyMessageBar } from "@skypointcloud/skypoint-ui";

export function ReadOnlyModeBanner() {
  const instanceId = getInstanceIdFromUrl() ?? "";

  // Only an operator-pack user actively in CRM mode should read /crm/me here.
  const { hasOperatorPack, currentMode, isLoading: packsLoading } = usePacks();
  const gated = !packsLoading && hasOperatorPack && currentMode === "operator_crm";

  const { data } = useGetCrmMeQuery({ instanceId }, { skip: !gated || !instanceId });

  if (!gated || !instanceId || data?.read_only !== true) return null;

  return (
    <div role="status">
      <SkyMessageBar
        intent="warning"
        message={
          <span>
            This tenant is in <strong>read-only (export-only)</strong> mode. Records cannot be
            created or changed; you can still view and export data.
          </span>
        }
      />
    </div>
  );
}
